import { CropInfoAtTime, TimeSpan } from '../types/video-feed-types';
import { SpeakersAnalysis } from '../types/speakers-analysis';
import { mergeWindows } from './rangeUtils';

export const buildCropInfoForSpeaker = (
  faceIds: number[],
  speakerAnalysis: SpeakersAnalysis,
  recordingEndTime: number
): CropInfoAtTime[] => {
  const { time_analysis, times } = speakerAnalysis;
  const rawCropInfo: CropInfoAtTime[] = [];

  time_analysis.forEach((analysis, index) => {
    const start = times[index];
    const end = index < times.length - 1 ? times[index + 1] : recordingEndTime;
    analysis.face_positions.forEach(fp => {
      if (!faceIds.includes(fp.face_id)) {
        return;
      }
      const cropPosition = fp.crop_position_person || fp.crop_position_face;
      if (!cropPosition) {
        return;
      }
      rawCropInfo.push({
        start,
        end,
        cropInfo: {
          top_left: cropPosition.top_left,
          bottom_right: cropPosition.bottom_right
        }
      });
    });
  });

  const nonOverlappingCropInfo: CropInfoAtTime[] = [];
  rawCropInfo.forEach(item => {
    const previous = nonOverlappingCropInfo[nonOverlappingCropInfo.length - 1];
    const start = previous ? Math.max(item.start, previous.end) : item.start;

    if (start >= item.end) {
      return;
    }

    nonOverlappingCropInfo.push({
      ...item,
      start
    });
  });

  return nonOverlappingCropInfo;
};

export const buildStageTimeSpansFromAnalysis = (
  faceIds: number[],
  speakerAnalysis: SpeakersAnalysis,
  recordingEndTime: number
): TimeSpan[] => {
  const { time_analysis, times } = speakerAnalysis;
  const ranges: TimeSpan[] = [];
  const faceIdRanges: Record<number, TimeSpan> = {};

  time_analysis.forEach((analysis, index) => {
    const currentTime = times[index];
    const nextTime = index < times.length - 1 ? times[index + 1] : recordingEndTime;

    analysis.face_positions.forEach(fp => {
      if (!faceIds.includes(fp.face_id)) {
        return;
      }
      if (!faceIdRanges[fp.face_id]) {
        faceIdRanges[fp.face_id] = { start: currentTime, end: nextTime };
      } else {
        faceIdRanges[fp.face_id].end = nextTime;
      }
    });
  });

  Object.values(faceIdRanges).forEach(range => {
    ranges.push(range);
  });

  const timeSpanTuples: [number, number][] = ranges.map(r => [r.start, r.end]);
  // Do not merge these spans. A speaker can have two detected faces, and merging
  // their timestamps can stretch a generated layout across the full combined
  // duration, which can cause us to miss switching to the second face.
  const mergedRanges = mergeWindows(timeSpanTuples);

  return mergedRanges.map(r => ({
    start: r[0],
    end: r[1]
  }));
};
